"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function RnInAction() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [likes, setLikes] = useState(0);
    const [following, setFollowing] = useState(false);
    const [flash, setFlash] = useState(false);
    const flashTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (flashTimer.current) clearTimeout(flashTimer.current);
        };
    }, []);

    function tapLike() {
        setLikes((l) => l + 1);
        setFlash(true);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(false), 650);
    }

    return (
        <section id="rn-in-action" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Rn_inaction_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Rn_inaction_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-red-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:react"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        App.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Rn_inaction_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8 grid lg:grid-cols-2 gap-8 items-center">
                    {/* Code */}
                    <div className="min-w-0 order-2 lg:order-1">
                        <pre className="rounded-2xl bg-black/40 ring-1 ring-white/10 p-4 text-[12.5px] leading-6 font-mono overflow-x-auto">
                            <code>
                                <span className="text-fuchsia-400">function </span>
                                <span className="text-sky-400">Profil</span>
                                <span className="text-gray-400">() {"{"}</span>
                                {"\n  "}
                                <span className="text-fuchsia-400">const </span>
                                <span className="text-gray-300">[</span>
                                <span className="text-sky-200">likes</span>
                                <span className="text-gray-300">, </span>
                                <span className="text-sky-200">setLikes</span>
                                <span className="text-gray-300">] = </span>
                                <span className="text-sky-400">useState</span>
                                <span className="text-gray-300">(</span>
                                <span className="text-amber-300">0</span>
                                <span className="text-gray-300">)</span>
                                {"\n  "}
                                <span className="text-fuchsia-400">return </span>
                                <span className="text-gray-400">(</span>
                                {"\n    "}
                                <span className="text-gray-500">{"<"}</span>
                                <span className="text-sky-400">Pressable</span>
                                {"\n      "}
                                <motion.span
                                    animate={
                                        flash && !reduce
                                            ? {
                                                  backgroundColor: [
                                                      "rgba(248,113,113,0)",
                                                      "rgba(248,113,113,0.25)",
                                                      "rgba(248,113,113,0)",
                                                  ],
                                              }
                                            : {}
                                    }
                                    transition={{ duration: 0.65 }}
                                    className="rounded px-0.5"
                                >
                                    <span className="text-gray-300">onPress</span>
                                    <span className="text-gray-500">={"{"}() {"=>"} </span>
                                    <span className="text-sky-200">setLikes</span>
                                    <span className="text-gray-300">(</span>
                                    <span className="text-sky-200">likes</span>
                                    <span className="text-gray-300"> + </span>
                                    <span className="text-amber-300">1</span>
                                    <span className="text-gray-300">)</span>
                                    <span className="text-gray-500">{"}"}</span>
                                </motion.span>
                                <span className="text-gray-500">{">"}</span>
                                {"\n      "}
                                <span className="text-gray-500">{"<"}</span>
                                <span className="text-sky-400">Text</span>
                                <span className="text-gray-500">{">"}</span>
                                <span className="text-rose-400"> ♥ </span>
                                <span className="text-gray-300">{"{"}</span>
                                <span className="text-sky-200">likes</span>
                                <span className="text-gray-300">{"}"}</span>
                                {"\n    "}
                                <span className="text-gray-500">{"</"}</span>
                                <span className="text-sky-400">Pressable</span>
                                <span className="text-gray-500">{">"}</span>
                                {"\n  "}
                                <span className="text-gray-400">)</span>
                                {"\n"}
                                <span className="text-gray-400">{"}"}</span>
                            </code>
                        </pre>
                        <p className="mt-3 text-[13px] text-gray-400 leading-relaxed mb-0">
                            {t("Rn_inaction_hint")}
                        </p>
                    </div>

                    {/* Téléphone */}
                    <div className="min-w-0 order-1 lg:order-2 flex justify-center">
                        <div className="w-[238px] rounded-[2.3rem] bg-slate-950 ring-1 ring-white/15 shadow-2xl shadow-black/50 p-2.5">
                            <div className="relative rounded-[1.8rem] bg-white overflow-hidden">
                                {/* Notch + status bar */}
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-950 rounded-b-2xl z-10" />
                                <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[10px] font-semibold text-slate-700">
                                    <span>9:41</span>
                                    <span className="flex items-center gap-1">
                                        <span className="w-3.5 h-2 rounded-[2px] border border-slate-400" />
                                    </span>
                                </div>

                                {/* Contenu de l'appli */}
                                <div className="px-5 pb-6 pt-3">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-[13px] font-bold text-slate-900">
                                            {t("Rn_inaction_screen_title")}
                                        </span>
                                        <SolarIcon
                                            name="menu-dots-bold"
                                            className="w-4 h-4 text-slate-400"
                                        />
                                    </div>

                                    <div className="flex flex-col items-center text-center">
                                        <Image
                                            src="/images/bebot/bebot-profile.jpg"
                                            width={72}
                                            height={72}
                                            alt="BeBot"
                                            className="w-[72px] h-[72px] rounded-full object-cover ring-2 ring-red-100"
                                        />
                                        <div className="mt-2.5 text-[15px] font-bold text-slate-900">
                                            BeBot
                                        </div>
                                        <div className="text-[12px] text-slate-400">
                                            @bebot
                                        </div>

                                        <div className="mt-4 flex items-center gap-2.5 w-full">
                                            <button
                                                type="button"
                                                onClick={tapLike}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-100 py-2.5 text-[13px] font-semibold text-slate-700 active:scale-95 transition"
                                            >
                                                <motion.span
                                                    key={likes}
                                                    initial={
                                                        reduce || likes === 0
                                                            ? false
                                                            : { scale: 1.5 }
                                                    }
                                                    animate={{ scale: 1 }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 500,
                                                        damping: 14,
                                                    }}
                                                    className="inline-flex"
                                                >
                                                    <SolarIcon
                                                        name="heart-angle-bold"
                                                        className={`w-4 h-4 ${likes > 0 ? "text-red-500" : "text-slate-400"}`}
                                                    />
                                                </motion.span>
                                                <span className="tabular-nums">
                                                    {likes}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setFollowing((f) => !f)
                                                }
                                                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[13px] font-semibold transition active:scale-95 ${
                                                    following
                                                        ? "bg-slate-100 text-slate-600"
                                                        : "bg-red-500 text-white"
                                                }`}
                                            >
                                                <SolarIcon
                                                    name={
                                                        following
                                                            ? "check-circle-bold"
                                                            : "user-plus-rounded-bold"
                                                    }
                                                    className="w-4 h-4"
                                                />
                                                {following
                                                    ? t("Rn_inaction_following")
                                                    : t("Rn_inaction_follow")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Rn_inaction_caption")}
            </p>
        </section>
    );
}
